import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router';
import { useAppContext } from '@/providers';
import { useJuthoorAdminDashboard } from '@/hooks/use-juthoor-admin-dashboard';
import { getJuthoorPendingAdminApprovals, getJuthoorPricingHistory } from '@/lib/api/juthoor-admin';
import { getStatusBadgeColor } from '@/lib/utils/initiative-helpers';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { t } from '@/lib/i18n/messages';
import type { JuthoorInitiative, JuthoorTreePricing } from '@/types/juthoor';
import type { PaginationMeta } from '@/types/api';

type Tab = 'dashboard' | 'pending' | 'pricing';

function StatCard({ label, value, icon }: { label: string; value: string | number; icon: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700 flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <span className="material-icons-outlined text-primary text-lg">{icon}</span>
      </div>
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-xl font-bold text-gray-900 dark:text-white mt-0.5">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
      </div>
    </div>
  );
}

function DashboardTab({ locale }: { locale: 'ar' | 'en' }) {
  const { data, loading } = useJuthoorAdminDashboard();

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="h-20 rounded-2xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <StatCard label={isRTL(locale) ? 'إجمالي المبادرات' : 'Total Initiatives'} value={data.total_initiatives} icon="forest" />
      <StatCard label={isRTL(locale) ? 'المبادرات النشطة' : 'Active Initiatives'} value={data.active_initiatives} icon="check_circle" />
      <StatCard label={t(locale, 'pendingApprovals')} value={data.pending_approvals} icon="pending" />
      <StatCard label={t(locale, 'netzeroPurchasedTrees')} value={data.total_trees_purchased_netzero} icon="park" />
      <StatCard label={t(locale, 'totalClients')} value={data.total_clients} icon="group" />
      <StatCard label={t(locale, 'totalRevenue')} value={`${data.total_client_revenue.toLocaleString()} NC`} icon="payments" />
      <StatCard label={t(locale, 'totalProfit')} value={`${data.total_profit.toLocaleString()} NC`} icon="trending_up" />
      <StatCard label={t(locale, 'atharProfit')} value={`${data.athar_profit.toLocaleString()} NC`} icon="account_balance" />
      <StatCard label={t(locale, 'municipalityProfit')} value={`${data.municipality_profit.toLocaleString()} NC`} icon="location_city" />
    </div>
  );
}

function isRTL(locale: 'ar' | 'en') {
  return locale === 'ar';
}

function PendingTab({ locale }: { locale: 'ar' | 'en' }) {
  const [initiatives, setInitiatives] = useState<JuthoorInitiative[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const fetchPending = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const response = await getJuthoorPendingAdminApprovals({ page: p, per_page: 20 });
      if (response.success && response.data) {
        setInitiatives(response.data);
        setMeta(response.meta ?? null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(page); }, [fetchPending, page]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (initiatives.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        {isRTL(locale) ? 'لا توجد مبادرات بانتظار الموافقة' : 'No initiatives pending approval'}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {initiatives.map((initiative) => {
        const title = isRTL(locale)
          ? initiative.title_ar || initiative.title
          : initiative.title_en || initiative.title;
        const badgeColor = getStatusBadgeColor(initiative.status);
        return (
          <Link
            key={initiative.id}
            to={`/dashboard/admin/juthoor/initiatives/${initiative.id}`}
            className="block bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700 hover:border-primary/40 transition-colors"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-gray-900 dark:text-white text-sm">{title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{initiative.enabler.name}</p>
              </div>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badgeColor}`}>
                {initiative.status.replace(/_/g, ' ')}
              </span>
            </div>
          </Link>
        );
      })}
      {meta && meta.last_page > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50"
          >
            {isRTL(locale) ? 'السابق' : 'Prev'}
          </button>
          <span className="text-sm text-gray-500">{page} / {meta.last_page}</span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
            disabled={page === meta.last_page}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50"
          >
            {isRTL(locale) ? 'التالي' : 'Next'}
          </button>
        </div>
      )}
    </div>
  );
}

function PricingHistoryTab({ locale }: { locale: 'ar' | 'en' }) {
  const [pricings, setPricings] = useState<JuthoorTreePricing[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const fetchHistory = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const response = await getJuthoorPricingHistory({ page: p, per_page: 20 });
      if (response.success && response.data) {
        setPricings(response.data);
        setMeta(response.meta ?? null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHistory(page); }, [fetchHistory, page]);

  if (loading) return <LoadingSpinner />;

  if (pricings.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        {isRTL(locale) ? 'لا يوجد سجل تسعير' : 'No pricing history'}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 dark:text-gray-400 text-xs border-b border-gray-100 dark:border-gray-700">
              <th className="py-2 px-3 font-medium text-start">{t(locale, 'purchasePrice')}</th>
              <th className="py-2 px-3 font-medium text-start">{t(locale, 'sellingPrice')}</th>
              <th className="py-2 px-3 font-medium text-start">{t(locale, 'marginPerTree')}</th>
              <th className="py-2 px-3 font-medium text-start">{t(locale, 'totalTrees')}</th>
              <th className="py-2 px-3 font-medium text-start">{isRTL(locale) ? 'التاريخ' : 'Date'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {pricings.map((p) => (
              <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                <td className="py-2 px-3">{p.netzero_purchase_price.toFixed(2)}</td>
                <td className="py-2 px-3">{p.client_selling_price.toFixed(2)}</td>
                <td className="py-2 px-3">{p.margin_per_tree.toFixed(2)}</td>
                <td className="py-2 px-3">{p.total_trees.toLocaleString()}</td>
                <td className="py-2 px-3 text-gray-500">
                  {new Date(p.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {meta && meta.last_page > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50"
          >
            {isRTL(locale) ? 'السابق' : 'Prev'}
          </button>
          <span className="text-sm text-gray-500">{page} / {meta.last_page}</span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
            disabled={page === meta.last_page}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50"
          >
            {isRTL(locale) ? 'التالي' : 'Next'}
          </button>
        </div>
      )}
    </div>
  );
}

export default function JuthoorAdminPage() {
  const { locale } = useAppContext();
  const rtl = isRTL(locale);
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'dashboard', label: t(locale, 'juthoorAdminDashboard'), icon: 'dashboard' },
    { key: 'pending', label: t(locale, 'pendingApprovals'), icon: 'pending_actions' },
    { key: 'pricing', label: t(locale, 'pricingHistory'), icon: 'receipt_long' },
  ];

  return (
    <div className={`max-w-6xl mx-auto p-4 md:p-8 space-y-6 ${rtl ? 'text-right' : ''}`} dir={rtl ? 'rtl' : 'ltr'}>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
        {t(locale, 'juthoorAdminDashboard')}
      </h1>

      {/* Tab nav */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-1 flex gap-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-white dark:bg-gray-800 text-primary shadow-sm'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <span className="material-icons-outlined text-base">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'dashboard' && <DashboardTab locale={locale} />}
      {activeTab === 'pending' && <PendingTab locale={locale} />}
      {activeTab === 'pricing' && <PricingHistoryTab locale={locale} />}
    </div>
  );
}
